<script setup>
import {ref, computed} from 'vue';
import {usePortalContentAndDesignStore} from "../../../../../../stores/partners/portalContentAndDesignStore";
import EditableTextField from "../../../../portal-editable-fields/EditableTextField.vue";
import EditableResourceTextEditorField
    from "../../../../portal-editable-fields/resources/EditableResourceTextEditorField.vue";
import EditableResourceImageField
    from "../../../../portal-editable-fields/resources/EditableResourceImageField.vue";
import EditableResourceButtonField
    from "../../../../portal-editable-fields/resources/EditableResourceButtonField.vue";
import {useHoverTracker} from "../../../../../../composables/partners/useHoverTracker";

const { hoverContentActive, isHoverContentActive } = useHoverTracker();

const props = defineProps({
    resource: {
        type: Object,
        required: true,
    },
});

const portalContentAndDesignStore = usePortalContentAndDesignStore();

const hoverMainContentContainer = ref(false);

const alignmentClass = computed(() => {
    switch (props.resource.alignment) {
        case 'center': return 'justify-center text-center';
        case 'right': return 'justify-end text-right';
        default: return 'justify-start text-left';
    }
});

const headerLayoutClass = computed(() => {
    const position = props.resource.main_image_position || 'left';
    const alignment = props.resource.alignment || 'left';

    if (position === 'top') {
        if (alignment === 'center') return 'flex flex-col items-center gap-4';
        if (alignment === 'right') return 'flex flex-col items-end gap-4';
        return 'flex flex-col items-start gap-4';
    }

    if (alignment === 'center') return 'flex flex-col items-center gap-4';
    return 'flex items-center gap-6';
});

const imageSizeClass = computed(() => {
    if (props.resource.main_image_size === undefined && props.resource.main_image_retina_resize !== undefined) {
        return props.resource.main_image_retina_resize ? 'max-w-8' : 'max-w-16';
    }
    const size = props.resource.main_image_size || 'medium';
    switch (size) {
        case 'small':
        case '@2x': return 'max-w-8';
        case 'large': return 'max-w-24';
        default: return 'max-w-16';
    }
});

const borderColorClass = computed(() => {
    return portalContentAndDesignStore.getPageContentTextColorClass.replace('text-', 'border-').replace('custom-content-text', 'gray-200') + ' border-opacity-20';
});

const hasItemContent = (item) => {
    switch (item.type) {
        case 'text': return !!item.content;
        case 'image': return !!item.files?.content_block_image?.url;
        case 'contentBlock': return !!(item.title || item.content || item.files?.content_block_image?.url);
        case 'links': return item.links?.length > 0;
        case 'accordion': return item.items?.some(i => i.question && i.answer);
        case 'divider': return true;
        default: return false;
    }
};

const imageWidthClass = (block) => {
    const w = block.image_width || '1/2';
    const map = { '1/2': 'sm:w-1/2', '1/3': 'sm:w-1/3', '2/5': 'sm:w-2/5' };
    return map[w] || 'sm:w-1/2';
};

const imageClass = (block) => {
    const border = (block.image_border ?? true) ? 'border' : '';
    return `rounded ${border}`.trim();
};

const openAccordionItems = ref({});

if (props.resource.items?.length) {
    props.resource.items.forEach(item => {
        if (item.type === 'accordion' && item.first_item_open && item.items?.length) {
            const firstValid = item.items.find(i => i.question && i.answer);
            if (firstValid) {
                const key = `${item.unique_id}-${firstValid.unique_id ?? 0}`;
                openAccordionItems.value[key] = true;
            }
        }
    });
}

const toggleAccordionItem = (itemId, accItemId) => {
    const key = `${itemId}-${accItemId}`;
    openAccordionItems.value[key] = !openAccordionItems.value[key];
};

const isAccordionItemOpen = (itemId, accItemId) => {
    return !!openAccordionItems.value[`${itemId}-${accItemId}`];
};
</script>

<template>
    <div>
        <div :class="[headerLayoutClass, alignmentClass]"
             @mouseenter="hoverMainContentContainer = true"
             @mouseleave="hoverMainContentContainer = false"
             :id="resource.unique_id"
        >
            <editable-resource-image-field
                resource-image-path="main_image"
                :resource-content="resource"
                :img-class="imageSizeClass"
                img-name="main_image"
                :show-add-image-button="hoverMainContentContainer"
            />

            <div class="space-y-1.5">
                <editable-text-field
                    v-if="resource.title"
                    text-type="h3"
                    :class="['text-xl md:text-2xl font-semibold', portalContentAndDesignStore.getPageContentTextColorClass]"
                    v-model="resource.title"
                />
                <editable-resource-text-editor-field
                    v-if="resource.description"
                    :class="[
                        'partnero-page-content-section text-base md:text-lg text-opacity-80 space-y-1',
                        portalContentAndDesignStore.getPageContentTextColorClass
                    ]"
                    :resource="resource"
                    resource-field="description"
                />
            </div>
        </div>

        <div
            v-if="resource.items?.length"
            :class="[(resource.title || resource.description) ? 'mt-4 md:mt-6' : '']"
        >
            <div class="space-y-8">
                <template v-for="(item, index) in resource.items" :key="item.unique_id || index">
                    <!-- Text item -->
                    <div v-if="item.type === 'text' && item.content">
                        <editable-resource-text-editor-field
                            :resource="item"
                            resource-field="content"
                            :class="['partnero-page-content-block text-sm md:text-base space-y-2', portalContentAndDesignStore.getPageContentTextColorClass]"
                        />
                    </div>

                    <!-- Image item -->
                    <div v-else-if="item.type === 'image' && item.files?.content_block_image?.url"
                         :id="item.unique_id"
                         @mouseenter="hoverContentActive(item.unique_id, true)"
                         @mouseleave="hoverContentActive(item.unique_id, false)"
                    >
                        <editable-resource-image-field
                            :resource-content="item"
                            resource-image-path="files.content_block_image"
                            :img-class="`w-auto rounded ${(item.image_border ?? true) ? 'border' : ''}`"
                            img-name="content_block_image"
                            :show-add-image-button="isHoverContentActive(item.unique_id)"
                        />
                    </div>

                    <!-- Content block item -->
                    <div v-else-if="item.type === 'contentBlock' && hasItemContent(item)"
                         :id="item.unique_id"
                         :class="(item.image_position === 'left' || item.image_position === 'right')
                            ? `flex flex-col sm:flex-row gap-6 ${item.image_position === 'right' ? 'sm:flex-row-reverse' : ''}`
                            : 'space-y-4'"
                         @mouseenter="hoverContentActive(item.unique_id, true)"
                         @mouseleave="hoverContentActive(item.unique_id, false)"
                    >
                        <div v-if="(item.image_position === 'left' || item.image_position === 'right') && item.files?.content_block_image?.url"
                            :class="[imageWidthClass(item), 'flex-shrink-0']"
                        >
                            <editable-resource-image-field
                                :resource-content="item"
                                resource-image-path="files.content_block_image"
                                :img-class="imageClass(item)"
                                img-name="content_block_image"
                                :show-add-image-button="isHoverContentActive(item.unique_id)"
                            />
                        </div>

                        <div :class="(item.image_position === 'left' || item.image_position === 'right') ? 'flex-1 space-y-2' : 'space-y-2'">
                            <editable-text-field
                                v-if="item.title"
                                text-type="custom"
                                class="text-md md:text-lg font-semibold"
                                v-model="item.title"
                            />
                            <editable-resource-image-field
                                v-if="item.files?.content_block_image?.url && (!item.image_position || item.image_position === 'top')"
                                :resource-content="item"
                                resource-image-path="files.content_block_image"
                                :img-class="imageClass(item)"
                                img-name="content_block_image"
                                :show-add-image-button="isHoverContentActive(item.unique_id)"
                            />
                            <editable-resource-text-editor-field
                                v-if="item.content"
                                :class="['partnero-page-content-block text-sm md:text-base space-y-1', portalContentAndDesignStore.getPageContentTextColorClass]"
                                :resource="item"
                                resource-field="content"
                            />
                            <editable-resource-button-field
                                v-if="item.button"
                                :resource-content="item"
                                :class="['w-full sm:w-auto !mt-2', portalContentAndDesignStore.getPublicPageButtonCorners]"
                            />
                        </div>
                    </div>

                    <!-- Accordion item -->
                    <div v-else-if="item.type === 'accordion' && item.items?.length">
                        <div :class="['divide-y', borderColorClass]">
                            <template v-for="(accItem, accIndex) in item.items">
                                <div
                                    v-if="accItem.question && accItem.answer"
                                    :key="accItem.unique_id || accIndex"
                                    class="py-4 first:pt-0 last:pb-0"
                                >
                                    <button
                                        type="button"
                                        class="w-full flex items-center justify-between text-left gap-4 cursor-pointer"
                                        @click="toggleAccordionItem(item.unique_id, accItem.unique_id ?? accIndex)"
                                    >
                                        <span :class="['text-base md:text-lg font-semibold flex-1', portalContentAndDesignStore.getPageContentTextColorClass]">
                                            {{ accItem.question }}
                                        </span>
                                        <span :class="['flex-shrink-0 transition-transform duration-200', portalContentAndDesignStore.getPageContentTextColorClass]">
                                            <svg v-if="isAccordionItemOpen(item.unique_id, accItem.unique_id ?? accIndex)" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M6 18L18 6M6 6l12 12"/>
                                            </svg>
                                            <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 4v16m8-8H4"/>
                                            </svg>
                                        </span>
                                    </button>
                                    <div
                                        v-show="isAccordionItemOpen(item.unique_id, accItem.unique_id ?? accIndex)"
                                        class="mt-3 pr-9"
                                    >
                                        <div
                                            :class="['text-base opacity-80 partnero-page-content-section space-y-2', portalContentAndDesignStore.getPageContentTextColorClass]"
                                            v-html="accItem.answer"
                                        />
                                    </div>
                                </div>
                            </template>
                        </div>
                    </div>

                    <!-- Divider item -->
                    <div v-else-if="item.type === 'divider'">
                        <hr :class="borderColorClass" />
                    </div>

                    <!-- Links item -->
                    <div v-else-if="item.type === 'links' && item.links?.length">
                        <div class="flex flex-col gap-2">
                            <a
                                v-for="(link, linkIndex) in item.links"
                                :key="linkIndex"
                                :href="link.url"
                                target="_blank"
                                rel="noopener noreferrer"
                                :class="['group flex items-start gap-3 py-2', portalContentAndDesignStore.getPageContentTextColorClass]"
                            >
                                <svg class="w-4 h-4 flex-shrink-0 mt-1 opacity-60 group-hover:opacity-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                                </svg>
                                <div class="flex flex-col">
                                    <span class="group-hover:underline">{{ link.title }}</span>
                                    <span v-if="link.description" class="text-sm opacity-60">{{ link.description }}</span>
                                </div>
                            </a>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>
